import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BRAND } from '../../../data/brand/brand.data';
import { URLS } from '../../../data/navigation/navigation.data';
import { findStoreListing } from '../../../data/product/product-lookup.data';
import { Product } from '../../../model/product/product.model';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { CartService } from '../../../service/cart/cart.service';
import { StoreGuide, StoreGuidesComponent } from '../../../component/store/store-guides/store-guides.component';
import { StoreOrder, StoreOrderCopy, StoreOrderItem, StoreReview, StoreService } from '../../../service/store/store.service';
import { ToastService } from '../../../service/toast/toast.service';

/** the page's state: what the visitor sees above the fold */
export type OrderView = 'loading' | 'pending' | 'paid' | 'failed' | 'expired' | 'refunded' | 'disputed' | 'not-found' | 'error';

/**
 * one purchased copy, joined with the site's theme data (art, install steps).
 * Every copy is its own row and download: two Oceans and a Prague are copies
 * 1, 2 and 3, and each file is named after the order and its number.
 */
export interface OrderLine {
  item: StoreOrderItem;
  copy: StoreOrderCopy;
  product: Product | null;
  category: string;
  productName: string;
  platformName: string;
  platformLogo: string;
  /** a theme's color palette, not a theme port */
  palette: boolean;
  /** an app (2026-10-14), not a theme: the card keeps its name and links to its page */
  app: boolean;
  appUrl: string;
  /** the lifetime download cap is spent (support resets it) */
  exhausted: boolean;
  /** shown when few downloads are left */
  remainingNote: string | null;
  /** the item's first copy: its row carries the item's rating (one per line, not per copy) */
  firstOfItem: boolean;
}

/** poll the pending order this often, for this long, before showing the "still confirming" note */
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 3 * 60 * 1000;
/** re-fetch the order (fresh signed links) when a link is about to expire */
const LINK_REFRESH_MARGIN_MS = 60 * 1000;
/** tokens whose Purchase event already fired on this browser (the success page can be reopened) */
const PURCHASED_STORAGE_KEY = 'codigrate.purchased.v1';

/**
 * /order/:token - the order page: the download buttons for a paid order, one
 * per copy, each file named after the order and its copy number (the links
 * are signed for one hour and re-issued every time the page loads),
 * the install steps per platform, the receipt, and the polling that bridges
 * the seconds between Stripe's redirect and the webhook that marks the order
 * paid. The token is the order's secret; no account, no login. See
 * base-themes/STORE-DESIGN.md §3.4.
 */
@Component( {
  selector: 'app-order-page',
  templateUrl: './order-page.component.html',
  styleUrls: [ './order-page.component.scss' ],
} )
export class OrderPageComponent implements OnInit, OnDestroy {

  URLS = URLS;

  view: OrderView = 'loading';
  order: StoreOrder | null = null;
  lines: OrderLine[] = [];
  /** copies over the whole order; the "2 of 3" tag shows when it is more than one */
  copyCount = 0;
  /** Getting started, one per platform in the order, in order of appearance */
  guides: StoreGuide[] = [];
  /** the pending poll ran out (3 min) - the payment is most likely still processing on Stripe's side */
  stillConfirming = false;
  /** a manual refresh is running */
  refreshing = false;

  totalLabel: string | null = null;
  paidAtLabel: string | null = null;

  private token = '';
  private pollTimer: any = null;
  private pollStartedAt = 0;
  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private storeService: StoreService,
    private cartService: CartService,
    private toastService: ToastService,
    private analyticsService: AnalyticsService,
  ) {
  }

  ngOnInit(): void {
    this.subs.push(
      this.route.paramMap.subscribe( params => {
        const token = params.get( 'token' ) || '';
        if ( token === this.token ) return;
        this.token = token;
        this.stopPolling();
        this.view = 'loading';
        this.order = null;
        this.lines = [];
        // the SEO service titles the route with the token appended; keep the tab tidy
        this.titleService.setTitle( `Your Order - ${ BRAND.name }` );
        this.load( true );
      } ),
    );
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.subs.forEach( sub => sub.unsubscribe() );
  }

  // ------------------------------------------------------------------ view

  /** the order holds an app (2026-10-14), so the page says "downloads" and "files", not only "themes" */
  get hasApp(): boolean {
    return this.lines.some( line => line.app );
  }

  get title(): string {
    switch ( this.view ) {
      case 'loading': return 'Your order';
      case 'pending': return this.stillConfirming ? 'Still confirming your payment' : 'Confirming your payment…';
      case 'paid': return this.order?.free ? 'Your free themes are ready' : ( this.hasApp ? 'Thank you, your downloads are ready' : 'Thank you, your themes are ready' );
      case 'failed': return 'The payment did not go through';
      case 'expired': return 'This checkout has expired';
      case 'refunded': return 'This order was refunded';
      case 'disputed': return 'This order is on hold';
      case 'not-found': return 'We could not find this order';
      default: return 'Something went wrong';
    }
  }

  get subtitle(): string {
    switch ( this.view ) {
      case 'loading': return 'One moment';
      case 'pending':
        return this.stillConfirming
          ? 'Stripe is taking longer than usual. Your download links will also arrive by e-mail as soon as the payment clears.'
          : 'Stripe is confirming the payment. This usually takes a few seconds.';
      case 'paid': return `Order ${ this.order?.orderNumber } · download below, and check your inbox for the same links`;
      case 'failed': return 'Nothing was charged. Your cart is still here if you want to try again.';
      case 'expired': return 'Checkout sessions last one hour. Nothing was charged.';
      case 'refunded': return 'The downloads for this order are no longer available.';
      case 'disputed': return 'The payment for this order is being disputed with the card issuer, so its downloads are paused.';
      case 'not-found': return 'The link may be incomplete, or the order may have been placed with a different link.';
      default: return 'Please try again in a moment.';
    }
  }

  get emailLine(): string | null {
    return this.order?.email ? `We e-mailed these links to ${ this.order.email }.` : null;
  }

  /** a free order (2026-09-21): nothing paid, no receipt, no VAT; the Total reads Free */
  get free(): boolean {
    return !!this.order?.free;
  }

  /** "Download all": every copy in one zip, offered when there is more than one */
  get downloadAllUrl(): string | null {
    return this.order?.downloadAllUrl || null;
  }

  // --------------------------------------------------------------- actions

  /** the Download button: refreshes the signed link first when it is about to expire. */
  download( line: OrderLine, event: Event ): void {
    const item = line.item;
    const copy = line.copy;
    if ( !copy.downloadUrl || line.exhausted ) {
      event.preventDefault();
      return;
    }
    this.analyticsService.logEvent( 'Download Clicked', { sku: item.sku, orderNumber: this.order?.orderNumber, copy: copy.number, fileName: copy.fileName } );

    const expiresAt = copy.downloadExpiresAt ? Date.parse( copy.downloadExpiresAt ) : 0;
    if ( expiresAt && expiresAt - Date.now() < LINK_REFRESH_MARGIN_MS ) {
      event.preventDefault();
      this.refreshing = true;
      this.storeService.getOrder( this.token ).subscribe( {
        next: ( order ) => {
          this.refreshing = false;
          this.apply( order );
          const fresh = this.lines.find( l => l.item.id === item.id && l.copy.number === copy.number );
          if ( fresh?.copy.downloadUrl ) window.location.assign( fresh.copy.downloadUrl );
        },
        error: () => {
          this.refreshing = false;
          this.toastService.show( 'The download link could not be refreshed. Please reload the page.', { tone: 'error' } );
        },
      } );
    }
  }

  /** "Download all": every copy in one zip; the link is refreshed first when it is about to expire (2026-09-21). */
  downloadAll( event: Event ): void {
    const url = this.order?.downloadAllUrl;
    if ( !url ) {
      event.preventDefault();
      return;
    }
    this.analyticsService.logEvent( 'Download All Clicked', { orderNumber: this.order?.orderNumber, copies: this.copyCount } );
    const expiresAt = this.order?.downloadAllExpiresAt ? Date.parse( this.order.downloadAllExpiresAt ) : 0;
    if ( expiresAt && expiresAt - Date.now() < LINK_REFRESH_MARGIN_MS ) {
      event.preventDefault();
      this.refreshing = true;
      this.storeService.getOrder( this.token ).subscribe( {
        next: ( order ) => {
          this.refreshing = false;
          this.apply( order );
          if ( order?.downloadAllUrl ) window.location.assign( order.downloadAllUrl );
        },
        error: () => {
          this.refreshing = false;
          this.toastService.show( 'The download link could not be refreshed. Please reload the page.', { tone: 'error' } );
        },
      } );
    }
  }

  /** the "Check again" button of the still-confirming state. */
  refresh(): void {
    if ( this.refreshing ) return;
    this.refreshing = true;
    this.storeService.getOrder( this.token ).subscribe( {
      next: ( order ) => {
        this.refreshing = false;
        this.apply( order );
        if ( this.view === 'pending' ) this.toastService.show( 'Not confirmed yet. We will e-mail you as soon as it is.' );
      },
      error: ( err ) => {
        this.refreshing = false;
        this.applyError( err );
      },
    } );
  }

  /** a saved rating: kept on the item so a later refresh of the links keeps it */
  onReviewSaved( line: OrderLine, review: StoreReview ): void {
    line.item.review = review;
  }

    trackByCopy( index: number, line: OrderLine ): number {
    return line.copy.number;
  }

  // --------------------------------------------------------------- loading

  private load( first: boolean ): void {
    this.storeService.getOrder( this.token ).subscribe( {
      next: ( order ) => {
        this.apply( order );
        if ( first && this.view === 'pending' ) this.startPolling();
      },
      error: ( err ) => this.applyError( err ),
    } );
  }

  private apply( order: StoreOrder ): void {
    if ( !order?.ok ) {
      this.view = order?.error === 'not_found' ? 'not-found' : 'error';
      return;
    }
    this.order = order;
    this.lines = this.buildLines( order );
    this.copyCount = this.lines.length;
    this.guides = StoreGuidesComponent.guidesFor( this.lines.map( l => ( { platform: l.item.platform, platformName: l.platformName, platformLogo: l.platformLogo } ) ) );
    this.titleService.setTitle( `Order ${ order.orderNumber } - ${ BRAND.name }` );
    this.totalLabel = order.free ? 'Free' : this.storeService.formatPrice( order.amountTotal, order.currency );
    this.paidAtLabel = this.formatDate( order.paidAt || order.createdAt );

    switch ( order.status ) {
      case 'paid':
        this.view = 'paid';
        this.stopPolling();
        this.onPaid( order );
        break;
      case 'pending':
        this.view = 'pending';
        break;
      case 'failed':
      case 'expired':
      case 'refunded':
      case 'disputed':
        this.view = order.status;
        this.stopPolling();
        break;
      default:
        this.view = 'error';
        this.stopPolling();
    }
  }

  private applyError( err: any ): void {
    this.stopPolling();
    this.view = err?.status === 404 ? 'not-found' : 'error';
  }

  /**
   * first time this browser sees the order paid: empty those SKUs from the cart, log the landing
   * once. The order itself ("Purchase" / "Free Order", on the buyer's profile) is sent by the
   * server when the order is fulfilled (2026-09-21), so this browser only notes that the buyer
   * reached the downloads page.
   */
  private onPaid( order: StoreOrder ): void {
    for ( const item of order.items || [] ) {
      if ( this.cartService.has( item.sku ) ) this.cartService.remove( item.sku, 'order' );
    }

    const purchased = this.readPurchased();
    if ( purchased.indexOf( order.token ) === -1 ) {
      purchased.push( order.token );
      this.writePurchased( purchased.slice( -50 ) );
      this.analyticsService.logEvent( 'Order Page Viewed', {
        kind: order.free ? 'free' : 'paid',
        orderNumber: order.orderNumber,
        skus: ( order.items || [] ).map( i => i.sku ).join( ',' ),
        itemCount: ( order.items || [] ).length,
        freeItems: ( order.items || [] ).filter( i => i.free ).length,
        amountTotal: order.amountTotal,
        currency: order.currency,
        managedPayments: order.managedPayments,
      } );
    }

    // ?checkout=success has done its job (it only marks the landing from Stripe)
    if ( this.route.snapshot.queryParamMap.has( 'checkout' ) ) {
      this.router.navigate( [], { relativeTo: this.route, queryParams: {}, replaceUrl: true } );
    }
  }

  // --------------------------------------------------------------- polling

  private startPolling(): void {
    this.stopPolling();
    this.stillConfirming = false;
    this.pollStartedAt = Date.now();
    this.pollTimer = setInterval( () => {
      if ( Date.now() - this.pollStartedAt > POLL_MAX_MS ) {
        this.stopPolling();
        this.stillConfirming = true;
        return;
      }
      this.storeService.getOrder( this.token ).subscribe( {
        next: ( order ) => this.apply( order ),
        error: () => { /* transient - the next tick retries */ },
      } );
    }, POLL_INTERVAL_MS );
  }

  private stopPolling(): void {
    if ( this.pollTimer ) {
      clearInterval( this.pollTimer );
      this.pollTimer = null;
    }
  }

  // --------------------------------------------------------------- helpers

  /**
   * one row per copy, in order: the server numbers them across the whole
   * order (an older server sends no `copies`: then each item is one copy).
   */
  private buildLines( order: StoreOrder ): OrderLine[] {
    const lines: OrderLine[] = [];
    let next = 1;
    for ( const item of order.items || [] ) {
      const copies: StoreOrderCopy[] = item.copies?.length
        ? item.copies
        : [ { number: next, fileName: item.fileName, downloadUrl: item.downloadUrl, downloadExpiresAt: item.downloadExpiresAt } ];
      copies.forEach( ( copy, index ) => {
        lines.push( { ...this.buildLine( item, copy ), firstOfItem: index === 0 } );
        next = copy.number + 1;
      } );
    }
    return lines;
  }

  private buildLine( item: StoreOrderItem, copy: StoreOrderCopy ): Omit<OrderLine, 'firstOfItem'> {
    const listing = findStoreListing( item.sku );
    const remaining = item.downloadsRemaining;
    return {
      item,
      copy,
      product: listing?.product ?? null,
      category: listing?.category ?? '',
      productName: listing?.product?.name || item.name.split( ' for ' )[ 0 ] || item.product,
      platformName: listing?.platformName || item.platform,
      platformLogo: listing?.platformLogo || '',
      palette: !!listing?.palette,
      app: !!listing?.app,
      appUrl: listing?.appUrl ?? '',
      exhausted: remaining !== null && remaining !== undefined && remaining <= 0,
      remainingNote: remaining !== null && remaining !== undefined && remaining > 0 && remaining <= 3
        ? `${ remaining } download${ remaining === 1 ? '' : 's' } left. Contact us if you need more`
        : null,
    };
  }

  private formatDate( iso: string | null ): string | null {
    if ( !iso ) return null;
    const date = new Date( iso );
    if ( isNaN( date.getTime() ) ) return null;
    try {
      return new Intl.DateTimeFormat( 'en-US', { day: 'numeric', month: 'long', year: 'numeric' } ).format( date );
    } catch {
      return date.toDateString();
    }
  }

  private readPurchased(): string[] {
    try {
      const raw = localStorage.getItem( PURCHASED_STORAGE_KEY );
      const parsed = raw ? JSON.parse( raw ) : [];
      return Array.isArray( parsed ) ? parsed.filter( t => typeof t === 'string' ) : [];
    } catch {
      return [];
    }
  }

  private writePurchased( tokens: string[] ): void {
    try {
      localStorage.setItem( PURCHASED_STORAGE_KEY, JSON.stringify( tokens ) );
    } catch {
      // private mode / quota: the event may fire again on a reload, nothing worse
    }
  }
}
